import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Tipos de empresa por país.
 * Endpoint: `/settings/countries/{countryId}/type-companies`
 */
@Injectable({ providedIn: 'root' })
export class CompanyTypeService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/type-companies`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private mapItem(it: any): CompanyType {
    return {
      id: it.id,
      prefix: it.code || it.prefix || '',
      name: it.name || it.title || '',
      status: normalizeCountryTitleStatus(it.status),
    };
  }

  getCompanyTypes(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<CompanyTypePageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as CompanyTypePageResponse;
        }),
      );
  }

  getCompanyTypeById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createCompanyType(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de empresa creado' })));
  }

  updateCompanyType(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de empresa actualizado' })));
  }

  deleteCompanyType(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Tipo de empresa eliminado' })));
  }
}

export interface CompanyType {
  id: string;
  prefix: string;
  name: string;
  status?: string;
}

export interface CompanyTypePageResponse {
  items: CompanyType[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

